import { populate as populatePlan } from '@go-client/lib/api/plan';
import {
  getDayTemplate,
  createEvent,
  updateEvent,
  deleteEvent,
  updateVenueData,
  createContent,
  updateContent,
  deleteContent,
} from '@go-client/lib/api/template';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import DailyPlanner, { DailyPlannerInfo } from '@go-client/components/DailyPlanner';
import { BareEvent } from '@go-shared/types/event';
import { MenuBar } from '@go-client/components';
import Page from './Page';
import { VenueDisposition } from '@go-shared/types/plan';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { BareSimpleSailing, BareSimpleSailingDay } from '@go-shared/types/sailing';
import PageWrapper from './PageWrapper';
import { resolvePlannerPath, resolveTemplateFeedPath } from '@go-client/routes';
import { BareContent } from '@go-shared/types/content';
import { getPropertyEventTemplates } from '@go-client/lib/api/eventTemplate';
import { getVenueTemplatesByPropertyId } from '@go-client/lib/api/venueTemplate';
import { getStaffTemplatesByPropertyId } from '@go-client/lib/api/staffTemplate';
import { populateEventTemplate } from '@go-shared/lib/eventTemplate';
import { populateVenueTemplate } from '@go-shared/lib/venue';
import { populateStaffTemplate } from '@go-shared/lib/staff';
import { getPropertyContentTemplates } from '@go-client/lib/api/contentTemplate';
import { getTranslations } from '@go-client/lib/api/translation';

export interface TemplateDailyPlannerParams {
  template: string;
}

type TemplateDailyPlannerPageProps = RouteComponentProps<TemplateDailyPlannerParams>;

export default function TemplateDailyPlanner(props: TemplateDailyPlannerPageProps) {
  const [plannerInfo, setPlannerInfo] = useState<DailyPlannerInfo | undefined>(undefined);

  const { template: templateId } = props.match.params;

  const { activeProperty } = useActiveProperty();

  useEffect(() => {
    loadTemplate({ includeLibraries: true, includeTranslations: true }).catch(console.error);
  }, [templateId]);

  async function loadTemplate(opt?: { includeLibraries?: boolean; includeTranslations?: boolean }) {
    const template = await getDayTemplate(templateId);

    if (!template) {
      throw new Error(`Could not find template ${templateId}`);
    }

    let barePlan = template.plan;
    let plan = populatePlan(barePlan, activeProperty);

    let eventTmpls = plannerInfo?.eventTmpls ?? [];
    let venueTmpls = plannerInfo?.venueTmpls ?? [];
    let contentTmpls = plannerInfo?.contentTmpls ?? [];
    let staffTmpls = plannerInfo?.staffTmpls ?? [];
    let eventTmplTransls = plannerInfo?.eventTmplTransls ?? [];
    let contentTmplTransls = plannerInfo?.contentTmplTransls ?? [];
    let venueTmplTransls = plannerInfo?.venueTmplTransls ?? [];
    let commonTextTransls = plannerInfo?.commonTextTransls ?? [];

    if (opt?.includeLibraries) {
      const [bareEventTemplates, bareVenueTemplates, bareContentTemplates, bareStaffTemplates] =
        await Promise.all([
          getPropertyEventTemplates(plan.property._id),
          getVenueTemplatesByPropertyId(plan.property._id),
          getPropertyContentTemplates(plan.property._id),
          getStaffTemplatesByPropertyId(plan.property._id),
        ]);

      eventTmpls = bareEventTemplates.map(et => populateEventTemplate(et, plan.property.brand));
      venueTmpls = bareVenueTemplates.map(vt => populateVenueTemplate(vt, plan.property));
      contentTmpls = bareContentTemplates;
      staffTmpls = bareStaffTemplates.map(st => populateStaffTemplate(st, plan.property));
    }

    if (opt?.includeTranslations) {
      const [
        eventTemplateTranslations,
        venueTemplateTranslations,
        contentTemplateTranslations,
        commonTextTranslations,
      ] = await Promise.all([
        getTranslations(plan.property._id, 'eventtemplate'),
        getTranslations(plan.property._id, 'venuetemplate'),
        getTranslations(plan.property._id, 'contenttemplate'),
        getTranslations(plan.property._id, 'commonText'),
      ]);

      eventTmplTransls = eventTemplateTranslations;
      venueTmplTransls = venueTemplateTranslations;
      contentTmplTransls = contentTemplateTranslations;
      commonTextTransls = commonTextTranslations;
    }

    const currFeedLink = resolveTemplateFeedPath(plan.property._id, template._id);

    setPlannerInfo({
      plan,
      template,
      eventTmpls,
      venueTmpls,
      contentTmpls,
      staffTmpls,
      eventTmplTransls,
      contentTmplTransls,
      venueTmplTransls,
      commonTextTransls,
      pathToFeed: currFeedLink,
    });
  }

  function handleOnCreateEvent(event: BareEvent, applyStaffToPlan?: boolean) {
    return createEvent(templateId, event, applyStaffToPlan);
  }

  function handleOnUpdateEvent(event: BareEvent, applyStaffToPlan?: boolean) {
    return updateEvent(templateId, event, applyStaffToPlan);
  }

  function handleOnDeleteEvent(eventId: string) {
    return deleteEvent(templateId, eventId);
  }

  function handleOnCreateContent(content: BareContent) {
    return createContent(templateId, content);
  }

  function handleOnUpdateContent(content: BareContent) {
    return updateContent(templateId, content);
  }

  function handleOnDeleteContent(contentId: string) {
    return deleteContent(templateId, contentId);
  }

  function handleOnUpdateVenueData(venueData: VenueDisposition) {
    return updateVenueData(templateId, venueData);
  }

  function handleOnSailingDayNameClick(
    sailing: BareSimpleSailing,
    sailingDay: BareSimpleSailingDay
  ) {
    props.history.push(resolvePlannerPath(activeProperty._id, sailing._id, sailingDay._id));
  }

  return (
    <PageWrapper {...props}>
      <Page className="DailyPlanner absolute flex items-stretch overflow-hidden">
        <MenuBar {...props} property={activeProperty} />
        <DailyPlanner
          variant="velvet"
          hideGroupEvents
          hideReserveEvents
          hideDayHeader
          plannerInfo={plannerInfo}
          onCreateEvent={handleOnCreateEvent}
          onUpdateEvent={handleOnUpdateEvent}
          onDeleteEvent={handleOnDeleteEvent}
          onUpdateVenueData={handleOnUpdateVenueData}
          onReloadPlan={loadTemplate}
          onSailingDayNameClick={handleOnSailingDayNameClick}
          onCreateContent={handleOnCreateContent}
          onUpdateContent={handleOnUpdateContent}
          onDeleteContent={handleOnDeleteContent}
        />
      </Page>
    </PageWrapper>
  );
}
